import { DEFAULT_CURRENCY } from "@/lib/format/money";
import { apiRequest } from "@/lib/api/http";
import { logCrud } from "@/lib/activity/emit-store";
import {
  costBoolean,
  costDate,
  costNullableNumber,
  costNumber,
  costText,
  oneOf,
  toRemoteAmount,
  toRemoteFlag,
} from "@/lib/api/cost-fields";
import { mapCostLogEntry, type RemoteCostLogEntry } from "@/lib/api/costs";
import type {
  CostIntervalUnit,
  CostLogEntry,
  CostRecurrence,
  CostRuleType,
} from "@/types/fleet";

/**
 * Upstream spells the combined rule `interval_and_mileage`; the domain type uses the camelCase
 * `intervalAndMileage` like every other field. Mapped explicitly in both directions rather than
 * by a generic snake/camel converter, so a typo fails the build instead of silently falling back.
 */
export const RULE_TYPE_FROM_REMOTE: Record<string, CostRuleType> = {
  interval: "interval",
  mileage: "mileage",
  interval_and_mileage: "intervalAndMileage",
  fixed_dates: "fixedDates",
};

const RULE_TYPE_TO_REMOTE: Record<CostRuleType, string> = {
  interval: "interval",
  mileage: "mileage",
  intervalAndMileage: "interval_and_mileage",
  fixedDates: "fixed_dates",
};

const INTERVAL_UNITS = [
  "day",
  "month",
  "year",
] as const satisfies readonly CostIntervalUnit[];

/** Upstream defaults, restated here so a rule created offline reads the same as a saved one. */
export const DEFAULT_LEAD_DAYS = 14;
export const DEFAULT_LEAD_KM = 500;

interface RemoteCostRecurrence {
  id: string;
  client_device_id?: string;
  cost_category_id?: string;
  title?: string;
  description?: string;
  vendor?: string;
  rule_type?: string;
  interval_value?: string | null;
  interval_unit?: string | null;
  mileage_interval_km?: string | null;
  fixed_dates?: string[] | null;
  anchor_date?: string;
  anchor_odometer?: string | null;
  estimated_amount?: string | null;
  currency?: string;
  lead_days?: string;
  lead_km?: string;
  is_active?: string;
  next_due_date?: string | null;
  next_due_odometer?: string | null;
  created_by?: string | null;
  created_by_name?: string;
  created_at?: string;
  updated_by?: string | null;
  updated_by_name?: string;
  updated_at?: string;
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

function mapCostRecurrence(rule: RemoteCostRecurrence): CostRecurrence {
  const ruleType = RULE_TYPE_FROM_REMOTE[costText(rule.rule_type)] ?? "interval";
  return {
    id: rule.id,
    vehicleId: costText(rule.client_device_id),
    categoryId: costText(rule.cost_category_id),
    title: costText(rule.title),
    description: costText(rule.description),
    vendor: costText(rule.vendor),
    ruleType,
    intervalValue: costNullableNumber(rule.interval_value),
    intervalUnit: rule.interval_unit
      ? oneOf(rule.interval_unit, INTERVAL_UNITS, "month")
      : null,
    mileageIntervalKm: costNullableNumber(rule.mileage_interval_km),
    fixedDates: Array.isArray(rule.fixed_dates) ? rule.fixed_dates.filter(Boolean) : [],
    anchorDate: costDate(rule.anchor_date),
    anchorOdometer: costNullableNumber(rule.anchor_odometer),
    estimatedAmount: costNullableNumber(rule.estimated_amount),
    currency: costText(rule.currency) || DEFAULT_CURRENCY,
    leadDays: rule.lead_days === undefined ? DEFAULT_LEAD_DAYS : costNumber(rule.lead_days),
    leadKm: rule.lead_km === undefined ? DEFAULT_LEAD_KM : costNumber(rule.lead_km),
    isActive: costBoolean(rule.is_active, true),
    nextDueDate: costDate(rule.next_due_date) || null,
    nextDueOdometer: costNullableNumber(rule.next_due_odometer),
    createdBy: costText(rule.created_by_name),
    createdAt: costDate(rule.created_at),
    updatedBy: costText(rule.updated_by_name),
    updatedAt: costDate(rule.updated_at),
  };
}

export interface CostRecurrenceInput {
  vehicleId: string;
  categoryId: string;
  title: string;
  ruleType: CostRuleType;
  description?: string;
  vendor?: string;
  intervalValue?: number | null;
  intervalUnit?: CostIntervalUnit | null;
  mileageIntervalKm?: number | null;
  fixedDates?: string[];
  anchorDate?: string;
  anchorOdometer?: number | null;
  estimatedAmount?: number | null;
  currency?: string;
  leadDays?: number;
  leadKm?: number;
  isActive?: boolean;
}

/**
 * Only the fields the chosen `ruleType` actually uses carry a value; the others are sent as
 * `null` so they are cleared. Upstream rejects a rule whose required basis is missing
 * (`400 invalid_rule`), and leaving a stale `mileage_interval_km` on a rule the user just switched
 * to `interval` would make it fire on mileage too.
 *
 * The rule block is sent only when `ruleType` is present, so a partial update that leaves the
 * schedule alone does not touch it. Callers editing any schedule field must pass `ruleType` too.
 */
function toRemoteRecurrence(input: Partial<CostRecurrenceInput>) {
  const ruleType = input.ruleType;
  const usesInterval = ruleType === "interval" || ruleType === "intervalAndMileage";
  const usesMileage = ruleType === "mileage" || ruleType === "intervalAndMileage";
  const usesFixedDates = ruleType === "fixedDates";

  return {
    ...(input.vehicleId !== undefined ? { client_device_id: input.vehicleId } : {}),
    ...(input.categoryId !== undefined ? { cost_category_id: input.categoryId } : {}),
    ...(input.title !== undefined ? { title: input.title } : {}),
    ...(ruleType !== undefined ? { rule_type: RULE_TYPE_TO_REMOTE[ruleType] } : {}),
    ...(input.description !== undefined ? { description: input.description } : {}),
    ...(input.vendor !== undefined ? { vendor: input.vendor } : {}),
    ...(ruleType !== undefined
      ? {
          interval_value:
            usesInterval && input.intervalValue ? String(input.intervalValue) : null,
          interval_unit: usesInterval ? (input.intervalUnit ?? "month") : null,
          mileage_interval_km:
            usesMileage && input.mileageIntervalKm ? String(input.mileageIntervalKm) : null,
          // A JSON array, never a string — upstream rejects the latter.
          fixed_dates: usesFixedDates ? (input.fixedDates ?? []) : null,
        }
      : {}),
    ...(input.anchorDate !== undefined ? { anchor_date: input.anchorDate } : {}),
    ...(input.anchorOdometer !== undefined
      ? { anchor_odometer: input.anchorOdometer === null ? "" : String(input.anchorOdometer) }
      : {}),
    ...(input.estimatedAmount !== undefined
      ? {
          estimated_amount:
            input.estimatedAmount === null ? "" : toRemoteAmount(input.estimatedAmount),
        }
      : {}),
    ...(input.currency !== undefined ? { currency: input.currency } : {}),
    ...(input.leadDays !== undefined ? { lead_days: String(input.leadDays) } : {}),
    ...(input.leadKm !== undefined ? { lead_km: String(input.leadKm) } : {}),
    ...(input.isActive !== undefined ? { is_active: toRemoteFlag(input.isActive) } : {}),
  };
}

export async function listCostRecurrences(vehicleId?: string): Promise<CostRecurrence[]> {
  const query = vehicleId ? `?client_device_id=${encodeURIComponent(vehicleId)}` : "";
  const response = await apiRequest<ListResponse<RemoteCostRecurrence>>(
    `/api/cost-recurrences${query}`,
    { method: "GET", cache: "no-store" },
  );
  return response.data.map(mapCostRecurrence);
}

export async function createCostRecurrence(input: CostRecurrenceInput): Promise<void> {
  const response = await apiRequest<{ success: true; id: string }>("/api/cost-recurrences", {
    method: "POST",
    body: JSON.stringify(toRemoteRecurrence(input)),
  });
  logCrud("create", "cost_recurrence", input.title, response.id);
}

export async function updateCostRecurrence(
  id: string,
  input: Partial<CostRecurrenceInput>,
): Promise<void> {
  await apiRequest<{ success: true }>(`/api/cost-recurrences/${id}`, {
    method: "PUT",
    body: JSON.stringify(toRemoteRecurrence(input)),
  });
  logCrud("update", "cost_recurrence", input.title ?? "", id);
}

export async function deleteCostRecurrence(id: string): Promise<void> {
  await apiRequest<{ success: true }>(`/api/cost-recurrences/${id}`, { method: "DELETE" });
  logCrud("delete", "cost_recurrence", "", id);
}

/** The rule's audit trail, including the entries for every cost it generated. Oldest first. */
export async function listCostRecurrenceLog(id: string): Promise<CostLogEntry[]> {
  const response = await apiRequest<ListResponse<RemoteCostLogEntry>>(
    `/api/cost-recurrences/${id}/log`,
    { method: "GET", cache: "no-store" },
  );
  return response.data.map(mapCostLogEntry);
}
